import {
  Building2,
  CarFront,
  CarTaxiFront,
  FileText,
  HeartPulse,
  ShieldCheck,
  WalletCards,
} from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Badge, Button, Card } from "../components/ui";
import { useAuth } from "../auth/AuthProvider";
import { useTenantRuntime } from "../config/TenantRuntime";
import { env } from "../lib/env";

export type PortalDemoState = "first-access" | "expired" | "denied" | "ready";

export function getPortalDemoState(value: string | null): PortalDemoState {
  if (value === "primeiro-acesso") return "first-access";
  if (value === "sessao-expirada") return "expired";
  if (value === "permissao-negada") return "denied";
  return "ready";
}

export function PortalPage() {
  const { tenant, hasModule } = useTenantRuntime();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const assetVertical = hasModule("assets");
  const [searchParams] = useSearchParams();
  const demoState =
    env.dataMode === "demo" ? getPortalDemoState(searchParams.get("estado")) : "ready";
  const isFirstAccess = demoState === "first-access";
  const isExpired = demoState === "expired";
  const isDenied = demoState === "denied";

  async function exitDemo() {
    await signOut();
    navigate("/login", { replace: true });
  }

  if (env.dataMode === "persistent") {
    return (
      <div className="public-page access-gate-page">
        <Card>
          <p className="eyebrow">PORTAL DO CLIENTE</p>
          <h1>Acesso ao portal em validação</h1>
          <p>
            Sua sessão foi confirmada, mas este portal só libera dados após o vínculo entre usuário,
            organização e conta do cliente ser validado pelo servidor.
          </p>
          <p className="muted-text">
            Os parâmetros de demonstração não são aceitos neste modo e nenhum dado de cliente foi
            carregado.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="public-page">
      <div className="portal-page">
        <header className="portal-header">
          <div>
            <p className="eyebrow">PORTAL DO CLIENTE</p>
            <h1>{tenant.branding.shortName}</h1>
            <p>
              {assetVertical
                ? "Acompanhe contratos, ativos, parcelas e pagamentos em um só lugar."
                : "Acompanhe seus contratos, benefícios e cobranças em um só lugar."}
            </p>
          </div>
          <Button variant="secondary" onClick={() => void exitDemo()}>
            Sair da demonstração
          </Button>
        </header>

        <div className="demo-warning" role="status">
          Ambiente de demonstração segura · dados fictícios · nenhuma ação externa será executada.
        </div>

        {isFirstAccess && (
          <Card>
            <Badge tone="warning">PRIMEIRO ACESSO</Badge>
            <h2>Configure seu primeiro acesso</h2>
            <p>
              Este fluxo é apenas demonstrativo. A criação de senha e a confirmação por código ainda
              não estão disponíveis.
            </p>
            <Link to="/portal">
              <Button variant="secondary">Voltar ao portal</Button>
            </Link>
          </Card>
        )}
        {isExpired && (
          <Card>
            <Badge tone="warning">SESSÃO EXPIRADA</Badge>
            <h2>É necessário entrar novamente</h2>
            <p>
              Por segurança, esta demonstração não mantém uma sessão real nem recupera credenciais.
            </p>
            <Link to="/login">
              <Button>Voltar ao acesso</Button>
            </Link>
          </Card>
        )}
        {isDenied && (
          <Card>
            <Badge tone="danger">PERMISSÃO NEGADA</Badge>
            <h2>Este conteúdo não está disponível</h2>
            <p>
              O perfil demonstrativo não possui permissão para abrir este recurso. Nenhum dado foi
              exposto.
            </p>
            <Link to="/portal">
              <Button variant="secondary">Voltar ao portal</Button>
            </Link>
          </Card>
        )}

        {!isFirstAccess && !isExpired && !isDenied && (
          <section className="portal-grid">
            {hasModule("funeral_assistance") && (
              <Card>
                <ShieldCheck size={24} />
                <h2>Plano funerário</h2>
                <Badge tone="success">ATIVO</Badge>
                <p>Consulte cobertura, carência, dependentes e forma de acionamento.</p>
                <Button variant="secondary">Ver cobertura</Button>
              </Card>
            )}

            {hasModule("telemedicine") && (
              <Card>
                <HeartPulse size={24} />
                <h2>Telemedicina</h2>
                <Badge tone="warning">INDISPONÍVEL NA DEMONSTRAÇÃO</Badge>
                <p>
                  O acesso clínico depende de consentimento e integração homologada. Nenhum link
                  clínico é emitido aqui.
                </p>
                <Link to="/portal?estado=permissao-negada">
                  <Button variant="secondary">Ver motivo</Button>
                </Link>
              </Card>
            )}

            {hasModule("vehicle_sales") && (
              <Card>
                <CarFront size={24} />
                <h2>Veículo comprado</h2>
                <Badge tone="success">CONTRATO ATIVO</Badge>
                <p>Consulte saldo, parcelas, documentos e situação da transferência.</p>
                <Button variant="secondary">Ver contrato</Button>
              </Card>
            )}

            {hasModule("property_leases") && (
              <Card>
                <Building2 size={24} />
                <h2>Imóvel alugado</h2>
                <Badge tone="success">LOCAÇÃO ATIVA</Badge>
                <p>Contrato, vencimentos, reajustes e comprovantes.</p>
                <Button variant="secondary">Ver locação</Button>
              </Card>
            )}

            {hasModule("vehicle_rentals") && (
              <Card>
                <CarTaxiFront size={24} />
                <h2>Veículo alugado</h2>
                <Badge tone="success">LOCAÇÃO ATIVA</Badge>
                <p>Pagamentos, quilometragem, inspeções e responsabilidades.</p>
                <Button variant="secondary">Ver veículo</Button>
              </Card>
            )}

            {hasModule("billing") && (
              <Card>
                <WalletCards size={24} />
                <h2>Financeiro</h2>
                <p>Pix, boleto, vencimentos e histórico de pagamentos.</p>
                {hasModule("public_payment_portal") ? (
                  <Link to="/segunda-via">
                    <Button>Emitir segunda via</Button>
                  </Link>
                ) : (
                  <Button>Ver cobranças</Button>
                )}
              </Card>
            )}

            <Card>
              <FileText size={24} />
              <h2>Documentos</h2>
              <Badge tone="warning">INDISPONÍVEL NA DEMONSTRAÇÃO</Badge>
              <p>Contratos, termos e comprovantes aparecerão após uma sessão autorizada.</p>
              <Link to="/portal?estado=permissao-negada">
                <Button variant="secondary">Ver motivo</Button>
              </Link>
            </Card>
          </section>
        )}
      </div>
    </div>
  );
}
